import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { useSetAtom } from 'jotai';

import { Company, ApiError } from '@restworld/utility-types';
import { useOrganizationsService } from '@restworld/data-services';
import { showToast } from '@restworld/ui-ds';
import { QUERY_KEYS } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { useEmployerCompanyListQueryKey } from './use-employer-company-list-query';
import { isEmployerCompanyActionModalOpenAtom, newlyCreatedCompanyIdAtom } from '../employer-companies.atoms';

function useEmployerCreateCompanyMutation(onSuccessCallback?: () => void) {
  const { t } = useTranslation('payment-and-invoices');
  const queryClient = useQueryClient();
  const organisationsService = useOrganizationsService();
  const { user } = useAuth();
  const setIsEmployerCompanyActionModalOpen = useSetAtom(isEmployerCompanyActionModalOpenAtom);
  const setNewlyCreatedCompanyId = useSetAtom(newlyCreatedCompanyIdAtom);
  const queryKey = useEmployerCompanyListQueryKey();
  const mutationKey = useEmployerCreateCompanyMutationKey();

  const mutation = useMutation({
    mutationFn: (company: Partial<Company>) => organisationsService.createCompany(company),
    onError: (error: ApiError) => {
      let errorMessage = t('something_went_wrong');
      if (error?.code === 422) {
        errorMessage = t('please_fill_in_all_required_fields_correctly');
      } else if (error?.code >= 500) {
        errorMessage = t('server_error');
      }
      showToast(errorMessage, 'error', { position: 'top-right', autoClose: 3000 });
    },
    onSuccess: (data) => {
      // Only set atom for flows that rely on it (Payments & Invoices, Purchase)
      // Don't set for callback-based flows (JP onboarding) to avoid cross-flow interference
      if (!onSuccessCallback) {
        data?.data?.id && setNewlyCreatedCompanyId(data.data.id);
      }

      showToast(t('new_company_created_successfully'), 'success', { position: 'top-right', autoClose: 3000 });

      // Invalidate both query keys to ensure all company lists are refetched
      queryClient.invalidateQueries({ queryKey });
      // Also invalidate the query key used by usePaymentCompanyData
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORGANISATIONS.COMPANY_LIST, user?.organization_id],
      });

      // Call custom callback if provided (for JP onboarding flow), otherwise close default modal
      if (onSuccessCallback) {
        onSuccessCallback();
      } else {
        setIsEmployerCompanyActionModalOpen(false);
      }
    },
    mutationKey,
  });

  return mutation;
}

export default useEmployerCreateCompanyMutation;

export function useEmployerCreateCompanyMutationKey() {
  return [QUERY_KEYS.ORGANISATIONS.CREATE_COMPANY];
}
